import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ImportWizard from '../components/ImportWizard';

const TOKEN = () => localStorage.getItem('crm_token');
const HEADERS = () => ({ Authorization: `Bearer ${TOKEN()}` });

const ROLE_LABELS = {
  owner:        { label: 'Sahip',    color: '#6366f1' },
  manager:      { label: 'Yönetici', color: '#8b5cf6' },
  senior_staff: { label: 'Kıdemli',  color: '#ec4899' },
  staff:        { label: 'Personel', color: '#10b981' },
  intern:       { label: 'Stajyer',  color: '#6b7280' },
};

const EMPTY_FORM = { first_name: '', last_name: '', email: '', phone: '', role: 'staff', password: '', show_in_calendar: true };

const ALL_MODULES = {
  base: [
    { key: 'calendar',         label: 'Randevular' },
    { key: 'preConsultations', label: 'Ön Görüşme Talepleri' },
    { key: 'customers',        label: 'Müşteriler' },
    { key: 'pos',              label: 'Kasa / POS' },
    { key: 'products',         label: 'Stok Yönetimi' },
    { key: 'settings',         label: 'Ayarlar' },
  ],
  beauty_salon: [
    { key: 'staff',    label: 'Personel' },
    { key: 'services', label: 'Hizmetler' },
  ],
  bridal_shop: [
    { key: 'measurements', label: 'Ölçü Kartı' },
    { key: 'fitting',      label: 'Prova Takvimi' },
    { key: 'rental',       label: 'Kiralama' },
  ],
  dusakabin: [
    { key: 'surveys',    label: 'Keşif / Ölçü' },
    { key: 'quotes',     label: 'Teklifler' },
    { key: 'production', label: 'Üretim Takibi' },
  ],
  wedding_hall: [
    { key: 'reservations',   label: 'Salon Rezervasyonları' },
    { key: 'packages',       label: 'Paketler' },
    { key: 'vendors',        label: 'Tedarikçiler' },
    { key: 'eventChecklist', label: 'Etkinlik Checklist' },
    { key: 'venueContracts', label: 'Sözleşmeler' },
  ],
};

const getModulesForIndustry = (industry) => [
  ...ALL_MODULES.base,
  ...(ALL_MODULES[industry] || []),
];

const FULL_ACCESS_ROLES = ['owner', 'manager'];

const WEEKDAY_LABELS = {
  mon: 'Pazartesi',
  tue: 'Salı',
  wed: 'Çarşamba',
  thu: 'Perşembe',
  fri: 'Cuma',
  sat: 'Cumartesi',
  sun: 'Pazar'
};

const DEFAULT_HOURS = {
  mon: { active: true, open: '09:00', close: '20:00' },
  tue: { active: true, open: '09:00', close: '20:00' },
  wed: { active: true, open: '09:00', close: '20:00' },
  thu: { active: true, open: '09:00', close: '20:00' },
  fri: { active: true, open: '09:00', close: '20:00' },
  sat: { active: true, open: '09:00', close: '18:00' },
  sun: { active: false, open: '00:00', close: '00:00' }
};

const DEFAULT_NOTIFICATIONS = {
  new_appointment: true,
  cancel_appointment: true,
  daily_summary: false,
  whatsapp_reminder: true,
  sms_reminder: false
};

const DEFAULT_INTEGRATIONS = {
  sms_provider: 'netgsm',
  netgsm_user: '',
  netgsm_pass: '',
  netgsm_title: '',
  whatsapp_token: '',
  whatsapp_phone_id: '',
  waha_url: '',
  waha_api_key: '',
  waha_session: 'default',
  smtp_host: '',
  smtp_port: '587',
  smtp_user: '',
  smtp_pass: '',
  smtp_from: '',
  smtp_secure: 'tls',
  salonappy_phone: '',
  salonappy_password: '',
  owner_notification_phone: '',
  owner_daily_summary_enabled: true,
  owner_daily_summary_time: '21:00'
};

function Settings() {
  const [branch, setBranch]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('general');
  const [saving, setSaving]           = useState(false);
  
  // Users & Staff
  const [staff, setStaff]             = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [modal, setModal]             = useState({ open: false, mode: 'add', data: null });
  const [form, setForm]               = useState(EMPTY_FORM);
  const [permissions, setPermissions] = useState([]);
  const [formSaving, setFormSaving]   = useState(false);
  const [formError, setFormError]     = useState('');

  // Data Export & Import States
  const [importing, setImporting]     = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [exporting, setExporting]     = useState(false);

  // Bildirim Cihazları (mobil uygulamadan kayıtlı FCM cihazları)
  const [deviceTokens, setDeviceTokens] = useState([]);
  const [deviceTokensLoading, setDeviceTokensLoading] = useState(false);
  const [testPushSending, setTestPushSending] = useState(false);
  const [testPushResult, setTestPushResult] = useState(null);

  useEffect(() => { fetchBranch(); }, []);
  useEffect(() => { if (activeTab === 'users') fetchStaff(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'notifications') fetchDeviceTokens(); }, [activeTab]);

  const fetchBranch = async () => {
    try {
      const { data } = await axios.get('/api/branches', { headers: HEADERS() });
      if (data.length > 0) {
        const rawBranch = data[0];
        
        // Deserialize JSON columns
        let working_hours = DEFAULT_HOURS;
        if (rawBranch.working_hours) {
          try {
            const parsed = typeof rawBranch.working_hours === 'string' 
              ? JSON.parse(rawBranch.working_hours) 
              : rawBranch.working_hours;
            
            const normalized = { ...DEFAULT_HOURS };
            Object.keys(normalized).forEach(day => {
              if (parsed[day]) {
                normalized[day] = {
                  active: parsed[day].active !== undefined 
                    ? parsed[day].active 
                    : (parsed[day].open !== '00:00' && parsed[day].open !== '' && parsed[day].open !== '00:00:00'),
                  open: parsed[day].open || '09:00',
                  close: parsed[day].close || '20:00'
                };
              }
            });
            working_hours = normalized;
          } catch (e) { working_hours = DEFAULT_HOURS; }
        }
        
        let notification_settings = DEFAULT_NOTIFICATIONS;
        if (rawBranch.notification_settings) {
          try {
            notification_settings = typeof rawBranch.notification_settings === 'string'
              ? JSON.parse(rawBranch.notification_settings)
              : rawBranch.notification_settings;
          } catch (e) { notification_settings = DEFAULT_NOTIFICATIONS; }
        }
        
        let integrations = DEFAULT_INTEGRATIONS;
        if (rawBranch.integrations) {
          try {
            integrations = typeof rawBranch.integrations === 'string'
              ? JSON.parse(rawBranch.integrations)
              : rawBranch.integrations;
          } catch (e) { integrations = DEFAULT_INTEGRATIONS; }
        }

        setBranch({
          ...rawBranch,
          working_hours,
          notification_settings,
          integrations
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStaff = async () => {
    setStaffLoading(true);
    try {
      const { data } = await axios.get('/api/staff', { headers: HEADERS() });
      setStaff(data);
    } catch (e) { console.error(e); }
    finally { setStaffLoading(false); }
  };

  const fetchDeviceTokens = async () => {
    setDeviceTokensLoading(true);
    try {
      const { data } = await axios.get('/api/auth/device-tokens', { headers: HEADERS() });
      setDeviceTokens(data.devices || []);
    } catch (e) { console.error(e); }
    finally { setDeviceTokensLoading(false); }
  };

  const handleTestPush = async () => {
    setTestPushSending(true);
    setTestPushResult(null);
    try {
      const { data } = await axios.post('/api/auth/test-push', {}, { headers: HEADERS() });
      setTestPushResult({ ok: true, message: `Gönderildi: ${data.result?.sent || 0} cihaza ulaştı.` });
    } catch (e) {
      setTestPushResult({ ok: false, message: e.response?.data?.error || 'Gönderilemedi' });
    }
    setTestPushSending(false);
  };

  const handleInputChange = (field, val) => {
    setBranch(prev => ({ ...prev, [field]: val }));
  };

  const handleHoursChange = (day, field, val) => {
    setBranch(prev => {
      const updatedHours = { ...prev.working_hours };
      updatedHours[day] = { ...updatedHours[day], [field]: val };
      return { ...prev, working_hours: updatedHours };
    });
  };

  const handleNotificationChange = (field, val) => {
    setBranch(prev => {
      const updatedNotifs = { ...prev.notification_settings, [field]: val };
      return { ...prev, notification_settings: updatedNotifs };
    });
  };

  const handleIntegrationChange = (field, val) => {
    setBranch(prev => {
      const updatedInts = { ...prev.integrations, [field]: val };
      return { ...prev, integrations: updatedInts };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/branches/${branch.id}`, branch, { headers: HEADERS() });
      alert('Ayarlar başarıyla kaydedildi!');
    } catch (e) {
      alert('Kayıt sırasında hata oluştu: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM); setFormError('');
    setPermissions([]);
    setModal({ open: true, mode: 'add', data: null });
  };

  const openEdit = (m) => {
    setForm({ first_name: m.first_name||'', last_name: m.last_name||'', email: m.email||'', phone: m.phone||'', role: m.role||'staff', password: '', show_in_calendar: m.show_in_calendar === undefined ? true : !!m.show_in_calendar });
    let perms = m.permissions;
    if (typeof perms === 'string') { try { perms = JSON.parse(perms); } catch { perms = []; } }
    setPermissions(Array.isArray(perms) ? perms : []);
    setFormError('');
    setModal({ open: true, mode: 'edit', data: m });
  };

  const closeModal = () => setModal({ open: false, mode: 'add', data: null });

  const handleFormSave = async () => {
    setFormError('');
    if (!form.first_name || !form.email) { setFormError('Ad ve e-posta zorunludur.'); return; }
    if (modal.mode === 'add' && !form.password) { setFormError('Şifre zorunludur.'); return; }
    setFormSaving(true);
    try {
      if (modal.mode === 'add') {
        await axios.post('/api/staff', { ...form, permissions }, { headers: HEADERS() });
      } else {
        const payload = { ...form, permissions };
        if (!payload.password) delete payload.password;
        await axios.put(`/api/staff/${modal.data.id}`, payload, { headers: HEADERS() });
      }
      closeModal(); fetchStaff();
    } catch (e) {
      setFormError(e.response?.data?.error || e.message);
    } finally { setFormSaving(false); }
  };

  const handleToggleActive = async (m) => {
    try {
      await axios.put(`/api/staff/${m.id}`, { is_active: !m.is_active }, { headers: HEADERS() });
      fetchStaff();
    } catch (e) { alert('Hata: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Data Export & Import ──
  const handleExportData = async (type) => {
    setExporting(true);
    try {
      const endpoint = type === 'customers' ? '/api/customers' : '/api/appointments';
      const { data } = await axios.get(endpoint, { headers: HEADERS() });
      
      const fileData = JSON.stringify(data, null, 2);
      const blob = new Blob([fileData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_export_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert('Dışa aktarma hatası: ' + e.message);
    } finally { setExporting(false); }
  };

  const handleImportCustomers = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const customers = JSON.parse(evt.target.result);
        if (!Array.isArray(customers)) {
          alert('Geçersiz format. Dosya bir müşteri dizisi içermelidir.');
          return;
        }

        setImporting(true);
        setImportStatus(`İçe aktarım başlıyor: ${customers.length} kayıt...`);

        let importedCount = 0;
        let errorCount = 0;

        for (let i = 0; i < customers.length; i++) {
          const c = customers[i];
          try {
            await axios.post('/api/customers', {
              first_name: c.first_name || '',
              last_name: c.last_name || '',
              phone: c.phone || '',
              email: c.email || '',
              gender: c.gender || 'female',
              notes: c.notes || ''
            }, { headers: HEADERS() });
            importedCount++;
            setImportStatus(`Aktarılıyor: ${importedCount}/${customers.length}...`);
          } catch (err) {
            errorCount++;
          }
        }

        setImportStatus(`Tamamlandı. Başarılı: ${importedCount}, Hatalı/Çift: ${errorCount}`);
        alert(`İçe aktarım tamamlandı!\nBaşarılı: ${importedCount}\nHatalı: ${errorCount}`);
      } catch (err) {
        alert('Dosya okuma veya JSON ayrıştırma hatası: ' + err.message);
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const tabs = [
    { id: 'general',       label: 'Genel',            icon: '⚙️' },
    { id: 'hours',         label: 'Çalışma Saatleri', icon: '🕐' },
    { id: 'notifications', label: 'Bildirimler',       icon: '🔔' },
    { id: 'users',         label: 'Kullanıcılar',      icon: '👥' },
    { id: 'data',          label: 'Veri Yönetimi',     icon: '💾' },
    { id: 'system',        label: 'Sistem',            icon: '🔧' },
  ];

  const initials = (m) => `${m.first_name?.[0]||''}${m.last_name?.[0]||''}`.toUpperCase();

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div className="settings-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Ayarlar</h1>
          <p className="page-subtitle">Sistem yapılandırması, entegrasyonlar ve kullanıcı yetkilendirmeleri</p>
        </div>
        {activeTab !== 'users' && activeTab !== 'data' && (
          <button className="btn btn-primary btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        )}
      </div>

      <div className="settings-layout">
        {/* ── Sidebar ── */}
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Content Pane ── */}
        <div className="settings-content">

          {/* ── General Settings ── */}
          {activeTab === 'general' && (
            <div className="card glass-card">
              <div className="card-header-icon">
                <span className="icon-badge">⚙️</span>
                <h3 className="card-title">Genel Ayarlar</h3>
              </div>
              
              <div className="logo-section">
                <div className="logo-preview-box">
                  {branch?.logo_url ? (
                    <img src={branch.logo_url} alt="Logo" className="logo-img-preview" />
                  ) : (
                    <span className="logo-placeholder">Logo Yok</span>
                  )}
                </div>
                <div style={{flex: 1}}>
                  <label className="form-label">Logo URL</label>
                  <input 
                    type="text" 
                    className="input" 
                    value={branch?.logo_url || ''} 
                    onChange={e => handleInputChange('logo_url', e.target.value)}
                    placeholder="https://ornek.com/logo.png"
                  />
                  <span className="form-hint">İşletmenizin logosunu URL olarak ekleyin.</span>
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Salon / Şube Adı</label>
                  <input 
                    type="text" 
                    className="input" 
                    value={branch?.name || ''} 
                    onChange={e => handleInputChange('name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input 
                    type="text" 
                    className="input" 
                    value={branch?.phone || ''} 
                    onChange={e => handleInputChange('phone', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">E-posta</label>
                  <input 
                    type="email" 
                    className="input" 
                    value={branch?.email || ''} 
                    onChange={e => handleInputChange('email', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Şehir</label>
                  <input 
                    type="text" 
                    className="input" 
                    value={branch?.city || ''} 
                    onChange={e => handleInputChange('city', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Saat Dilimi (Timezone)</label>
                  <select 
                    className="select" 
                    value={branch?.timezone || 'Europe/Istanbul'} 
                    onChange={e => handleInputChange('timezone', e.target.value)}
                  >
                    <option value="Europe/Istanbul">İstanbul (GMT+3)</option>
                    <option value="Europe/London">Londra (GMT+0)</option>
                    <option value="Europe/Paris">Paris (GMT+1)</option>
                    <option value="America/New_York">New York (GMT-5)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Sektör (Sadece Admin Değiştirebilir)</label>
                  <input type="text" className="input" value={branch?.industry || ''} disabled style={{opacity:0.6}} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Adres</label>
                <textarea 
                  className="textarea" 
                  rows={3} 
                  value={branch?.address || ''} 
                  onChange={e => handleInputChange('address', e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="card glass-card" style={{ marginTop: '20px' }}>
              <div className="card-header-icon">
                <span className="icon-badge">💬</span>
                <h3 className="card-title">Mesajlaşma Görünümü</h3>
              </div>
              <div className="notif-toggle-row">
                <div className="notif-text">
                  <span className="notif-label">Mesajlarda Ön Görüşme İşlemleri</span>
                  <span className="notif-desc">Açıkken, WhatsApp sohbetlerinin üstünde Ön Görüşme'deki Arandı / Cevap Yok / Tekrar Ara / Randevulandı / Tamamlandı / Kayıp butonları görünür (sohbete karşılık gelen bir ön görüşme talebi varsa).</span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={branch?.notification_settings?.show_precons_in_messages ?? false}
                    onChange={e => handleNotificationChange('show_precons_in_messages', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          )}

          {/* ── Working Hours ── */}
          {activeTab === 'hours' && (
            <div className="card glass-card">
              <div className="card-header-icon">
                <span className="icon-badge">🕐</span>
                <h3 className="card-title">Çalışma Saatleri</h3>
              </div>
              <p className="card-subtitle-desc">Müşterilerinizin randevu alabileceği çalışma gün ve saatlerini belirleyin.</p>
              
              <div className="hours-list">
                {Object.entries(branch?.working_hours || DEFAULT_HOURS).map(([day, val]) => (
                  <div key={day} className={`hours-row ${val.active ? 'active-day' : 'inactive-day'}`}>
                    <div className="hours-day-info">
                      <label className="switch">
                        <input 
                          type="checkbox" 
                          checked={val.active} 
                          onChange={e => handleHoursChange(day, 'active', e.target.checked)} 
                        />
                        <span className="slider round"></span>
                      </label>
                      <span className="day-name">{WEEKDAY_LABELS[day] || day}</span>
                    </div>
                    {val.active ? (
                      <div className="hours-time-picker">
                        <input 
                          type="time" 
                          className="input time-input" 
                          value={val.open} 
                          onChange={e => handleHoursChange(day, 'open', e.target.value)}
                        />
                        <span className="time-sep">-</span>
                        <input 
                          type="time" 
                          className="input time-input" 
                          value={val.close} 
                          onChange={e => handleHoursChange(day, 'close', e.target.value)}
                        />
                      </div>
                    ) : (
                      <span className="day-closed-badge">Kapalı</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Notification Settings ── */}
          {activeTab === 'notifications' && (
            <div className="card glass-card">
              <div className="card-header-icon">
                <span className="icon-badge">🔔</span>
                <h3 className="card-title">Bildirim Ayarları</h3>
              </div>
              <p className="card-subtitle-desc">Sistem içi ve müşterilerinize gönderilen otomatik bildirimlerin kuralları.</p>
              
              <div className="notifications-list">
                {[
                  ['new_appointment', 'Yeni Randevu Bildirimi', 'Yeni randevu oluşturulduğunda yöneticilere e-posta gönder.'],
                  ['cancel_appointment', 'Randevu İptal Bildirimi', 'Müşteri randevuyu iptal ettiğinde bildirim gönder.'],
                  ['daily_summary', 'Günlük Özet Raporu', 'Her gün saat 21:00\'de günlük performans özetini mail at.'],
                  ['whatsapp_reminder', 'WhatsApp Hatırlatıcısı', 'Randevudan 24 saat önce müşteriye otomatik WhatsApp hatırlatması yolla.'],
                  ['sms_reminder', 'SMS Hatırlatıcısı', 'Müşteriye SMS yoluyla randevu saati bildirimi yap.'],
                ].map(([field, label, desc]) => (
                  <div key={field} className="notif-toggle-row">
                    <div className="notif-text">
                      <span className="notif-label">{label}</span>
                      <span className="notif-desc">{desc}</span>
                    </div>
                    <label className="switch">
                      <input 
                        type="checkbox" 
                        checked={branch?.notification_settings?.[field] ?? false} 
                        onChange={e => handleNotificationChange(field, e.target.checked)} 
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card glass-card" style={{ marginTop: '20px' }}>
              <div className="card-header-icon">
                <span className="icon-badge">📱</span>
                <h3 className="card-title">Bildirim Cihazları</h3>
              </div>
              <p className="card-subtitle-desc">
                Mobil uygulamadan giriş yapan personelin bildirim için kayıtlı cihazları.
              </p>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
                <button
                  onClick={handleTestPush}
                  disabled={testPushSending}
                  className="btn"
                  style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3B82F6,#2563EB)', color: '#fff', fontWeight: 600, cursor: testPushSending ? 'default' : 'pointer' }}
                >
                  {testPushSending ? 'Gönderiliyor...' : 'Test Et'}
                </button>
                {testPushResult && (
                  <span style={{ fontSize: '13px', color: testPushResult.ok ? '#34d399' : '#f87171' }}>{testPushResult.message}</span>
                )}
              </div>

              {deviceTokensLoading ? (
                <p style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '14px' }}>Yükleniyor...</p>
              ) : deviceTokens.length === 0 ? (
                <p style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '14px' }}>
                  Henüz kayıtlı bir cihaz yok - personel mobil uygulamaya giriş yaptığında burada görünecek.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-muted, #94a3b8)', fontSize: '12px', textTransform: 'uppercase' }}>
                        <th style={{ padding: '8px' }}>Personel</th>
                        <th style={{ padding: '8px' }}>Platform</th>
                        <th style={{ padding: '8px' }}>Son Güncelleme</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceTokens.map(d => (
                        <tr key={d.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '8px' }}>
                            {d.first_name ? `${d.first_name} ${d.last_name || ''}`.trim() : `#${d.staff_id} (silinmiş)`}
                          </td>
                          <td style={{ padding: '8px' }}>{d.platform || '—'}</td>
                          <td style={{ padding: '8px' }}>
                            {d.updated_at ? new Date(d.updated_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Users Tab ── */}
          {activeTab === 'users' && (
            <div className="card glass-card">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                <div className="card-header-icon">
                  <span className="icon-badge">👥</span>
                  <div>
                    <h3 className="card-title" style={{margin:0}}>Kullanıcılar ve Yetkiler</h3>
                    <p style={{margin:'4px 0 0',fontSize:13,color:'#64748b'}}>Sistem kullanıcılarını yönetin</p>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={openAdd}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                  Yeni Kullanıcı
                </button>
              </div>

              {staffLoading ? (
                <div style={{textAlign:'center',padding:40,color:'#64748b'}}><div className="loading-spinner"></div></div>
              ) : staff.length === 0 ? (
                <div style={{textAlign:'center',padding:40,color:'#64748b'}}>Henüz kullanıcı yok</div>
              ) : (
                <div className="users-table">
                  <table className="table">
                    <thead><tr><th>Kullanıcı</th><th>Rol</th><th>Durum</th><th>İşlem</th></tr></thead>
                    <tbody>
                      {staff.map(member => {
                        const role = ROLE_LABELS[member.role] || ROLE_LABELS.staff;
                        return (
                          <tr key={member.id}>
                            <td>
                              <div style={{display:'flex',alignItems:'center',gap:12}}>
                                <div className="avatar avatar-sm" style={{background:member.color_code||role.color}}>
                                  {initials(member)}
                                </div>
                                <div>
                                  <div style={{fontWeight:600}}>{member.first_name} {member.last_name}</div>
                                  <div style={{fontSize:12,color:'#64748b'}}>{member.email}</div>
                                  {member.phone && <div style={{fontSize:12,color:'#94a3b8'}}>{member.phone}</div>}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge" style={{background:role.color+'22',color:role.color,fontWeight:600}}>
                                {role.label}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${member.is_active?'badge-success':'badge-gray'}`}>
                                {member.is_active ? 'Aktif' : 'Pasif'}
                              </span>
                            </td>
                            <td>
                              <div style={{display:'flex',gap:6}}>
                                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(member)}>Düzenle</button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{color: member.is_active ? '#ef4444' : '#10b981'}}
                                  onClick={() => handleToggleActive(member)}
                                >
                                  {member.is_active ? 'Pasife Al' : 'Aktif Et'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Data Management Tab ── */}
          {activeTab === 'data' && (
            <div className="card glass-card">
              <div className="card-header-icon">
                <span className="icon-badge">💾</span>
                <h3 className="card-title">Veri Yönetimi ve İçe Aktarma</h3>
              </div>
              <p className="card-subtitle-desc">CSV veya Excel dosyasınızdan müşteri, hizmet, ürün veya randevu verilerini içe aktarın.</p>

              {/* Export section */}
              <div className="sub-card" style={{marginBottom: 8}}>
                <h4 className="card-sub-title">📤 Dışa Aktar (Export)</h4>
                <p className="sub-desc">Sistemdeki tüm verileri JSON dosyası olarak bilgisayarınıza indirin.</p>
                <div className="btn-group-row">
                  <button className="btn btn-outline" onClick={() => handleExportData('customers')} disabled={exporting}>Müşterileri İndir</button>
                  <button className="btn btn-outline" onClick={() => handleExportData('appointments')} disabled={exporting}>Randevuları İndir</button>
                </div>
              </div>

              {/* Import Wizard */}
              <div style={{marginTop: 8}}>
                <h4 className="card-sub-title" style={{marginBottom: 16}}>📥 Veri İçe Aktar (Import Wizard)</h4>
                <ImportWizard />
              </div>
            </div>
          )}

          {/* ── System Tab ── */}
          {activeTab === 'system' && (
            <div className="card glass-card">
              <div className="card-header-icon">
                <span className="icon-badge">🔧</span>
                <h3 className="card-title">Sistem Bilgileri</h3>
              </div>
              <div className="system-info">
                {[
                  ['Uygulama Sürümü','v2.1.0 (Haziran 2026)'],
                  ['Veritabanı Motoru','MySQL 8.0 / InnoDB'],
                  ['Çalışma Modu','Geliştirici Sürümü'],
                  ['Aktif Sektör Modülü',branch?.industry || 'beauty_salon'],
                  ['Varsayılan Kur', 'TRY (₺)'],
                  ['API Port','3002']
                ].map(([l,v]) => (
                  <div key={l} className="info-row">
                    <span className="info-label">{l}</span>
                    <span className="info-value">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Users Edit Modal ── */}
      {modal.open && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <h3 style={{margin:0,fontSize:17,fontWeight:700}}>
                {modal.mode === 'add' ? 'Yeni Kullanıcı Ekle' : 'Kullanıcıyı Düzenle'}
              </h3>
              <button onClick={closeModal} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#94a3b8',lineHeight:1}}>×</button>
            </div>

            {formError && (
              <div className="modal-error-box">
                {formError}
              </div>
            )}

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[['first_name','Ad *','text'],['last_name','Soyad','text'],['email','E-posta *','email'],['phone','Telefon','tel']].map(([key,lbl,type]) => (
                <div key={key} className="form-group" style={{margin:0}}>
                  <label className="form-label">{lbl}</label>
                  <input
                    type={type}
                    className="input"
                    value={form[key]}
                    onChange={e => setForm(f => ({...f,[key]:e.target.value}))}
                  />
                </div>
              ))}
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:12}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Rol</label>
                <select className="select" value={form.role} onChange={e => setForm(f => ({...f,role:e.target.value}))}>
                  {Object.entries(ROLE_LABELS).map(([k,v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">
                  {modal.mode === 'edit' ? 'Yeni Şifre (boş=değişmez)' : 'Şifre *'}
                </label>
                <input
                  type="password"
                  className="input"
                  value={form.password}
                  onChange={e => setForm(f => ({...f,password:e.target.value}))}
                  placeholder={modal.mode === 'edit' ? '••••••' : 'En az 6 karakter'}
                />
              </div>
            </div>

            <div className="form-group" style={{display:'flex',alignItems:'center',gap:8,marginTop:12}}>
              <input
                type="checkbox"
                id="showInCalendarCheck"
                checked={form.show_in_calendar}
                onChange={e => setForm(f => ({...f, show_in_calendar: e.target.checked}))}
                style={{width:16,height:16,cursor:'pointer'}}
              />
              <label htmlFor="showInCalendarCheck" style={{cursor:'pointer',fontSize:13,color:'var(--text)'}}>
                Randevular takviminde sütun olarak görünsün
              </label>
            </div>

            {/* Panel Erişim İzinleri */}
            {!FULL_ACCESS_ROLES.includes(form.role) && (
              <div style={{marginTop:16,padding:'14px 16px',background:'var(--bg-sub)',borderRadius:12,border:'1px solid var(--border)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <span style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>🔐 Panel Erişim İzinleri</span>
                  <div style={{display:'flex',gap:8}}>
                    <button
                      type="button"
                      style={{fontSize:11,color:'var(--tertiary)',background:'none',border:'none',cursor:'pointer',fontWeight:600}}
                      onClick={() => setPermissions(getModulesForIndustry(branch?.industry).map(m => m.key))}
                    >Tümünü Seç</button>
                    <span style={{color:'var(--border)'}}>|</span>
                    <button
                      type="button"
                      style={{fontSize:11,color:'#ef4444',background:'none',border:'none',cursor:'pointer',fontWeight:600}}
                      onClick={() => setPermissions([])}
                    >Temizle</button>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {getModulesForIndustry(branch?.industry).map(mod => (
                    <label key={mod.key} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'6px 10px',borderRadius:8,background:permissions.includes(mod.key)?'rgba(var(--secondary-rgb), 0.15)':'var(--bg-card)',border:'1px solid',borderColor:permissions.includes(mod.key)?'var(--accent)':'var(--border)',transition:'all .15s'}}>
                      <input
                        type="checkbox"
                        checked={permissions.includes(mod.key)}
                        onChange={e => {
                          setPermissions(prev =>
                            e.target.checked
                              ? [...prev, mod.key]
                              : prev.filter(k => k !== mod.key)
                          );
                        }}
                        style={{width:15,height:15,accentColor:'var(--tertiary)'}}
                      />
                      <span style={{fontSize:13,fontWeight:500,color:permissions.includes(mod.key)?'var(--primary)':'var(--text)'}}>{mod.label}</span>
                    </label>
                  ))}
                </div>
                <p style={{margin:'8px 0 0',fontSize:11,color:'#94a3b8'}}>
                  {ROLE_LABELS[form.role]?.label || 'Bu rol'} — sadece seçili panellere erişebilir.
                </p>
              </div>
            )}
            {FULL_ACCESS_ROLES.includes(form.role) && (
              <div style={{marginTop:16,padding:'10px 14px',background:'var(--success-light)',borderRadius:10,border:'1px solid rgba(16, 185, 129, 0.2)',fontSize:13,color:'var(--success)'}}>
                ✅ <strong>{ROLE_LABELS[form.role]?.label}</strong> rolü tüm panellere otomatik olarak erişebilir.
              </div>
            )}

            {/* Randevu Yetkileri - varsayılan (izinler hiç dokunulmamışsa) serbest,
                sadece owner bu personel için elle kısıtlarsa devreye girer. */}
            {!FULL_ACCESS_ROLES.includes(form.role) && (
              <div style={{marginTop:16,padding:'14px 16px',background:'var(--bg-sub)',borderRadius:12,border:'1px solid var(--border)'}}>
                <span style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>📅 Randevu Yetkileri</span>
                <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:10}}>
                  {[
                    { key: 'edit_appointments', label: 'Randevuları düzenleyebilir (personel/saat değiştirme)' },
                    { key: 'delete_appointments', label: 'Randevuları silebilir / iptal edebilir' }
                  ].map(perm => (
                    <label key={perm.key} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'6px 10px',borderRadius:8,background:permissions.includes(perm.key)?'rgba(var(--secondary-rgb), 0.15)':'var(--bg-card)',border:'1px solid',borderColor:permissions.includes(perm.key)?'var(--accent)':'var(--border)',transition:'all .15s'}}>
                      <input
                        type="checkbox"
                        checked={permissions.includes(perm.key)}
                        onChange={e => {
                          setPermissions(prev =>
                            e.target.checked
                              ? [...prev, perm.key]
                              : prev.filter(k => k !== perm.key)
                          );
                        }}
                        style={{width:15,height:15,accentColor:'var(--tertiary)'}}
                      />
                      <span style={{fontSize:13,fontWeight:500,color:permissions.includes(perm.key)?'var(--primary)':'var(--text)'}}>{perm.label}</span>
                    </label>
                  ))}
                </div>
                <p style={{margin:'8px 0 0',fontSize:11,color:'#94a3b8'}}>
                  Bu personel randevu ekranından her zaman SADECE KENDİ randevularını
                  girip görüntüleyebilir. Bu personel için hiçbir izin (panel erişimi
                  dahil) hiç özelleştirilmediyse randevu düzenleme/silme serbesttir;
                  izinler bir kez özelleştirilince yukarıdaki kutular geçerli olur.
                </p>
              </div>
            )}

            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:24}}>
              <button className="btn btn-ghost" onClick={closeModal} disabled={formSaving}>İptal</button>
              <button className="btn btn-primary" onClick={handleFormSave} disabled={formSaving}>
                {formSaving ? 'Kaydediliyor...' : modal.mode === 'add' ? 'Ekle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .settings-container {
          animation: fadeIn 0.4s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .settings-layout {
          display: flex;
          gap: 24px;
          margin-top: 20px;
        }
        .settings-sidebar {
          width: 240px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .settings-tab {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 14px;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          border-radius: 12px;
          transition: all 0.2s ease;
          width: 100%;
        }
        .settings-tab:hover {
          background: var(--bg-card-hover);
          color: var(--text);
        }
        .settings-tab.active {
          background: linear-gradient(135deg, var(--tertiary), var(--secondary));
          color: white;
          box-shadow: 0 4px 12px rgba(var(--tertiary-rgb), 0.25);
        }
        .settings-content {
          flex: 1;
        }
        .glass-card {
          background: rgba(12, 15, 25, 0.85);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
        }
        .card-header-icon {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }
        .icon-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: rgba(var(--tertiary-rgb), 0.1);
          border-radius: 10px;
          font-size: 18px;
        }
        .card-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
          margin: 0;
        }
        .card-subtitle-desc {
          font-size: 13px;
          color: #64748b;
          margin-top: -10px;
          margin-bottom: 20px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }
        .logo-preview-box {
          width: 80px;
          height: 80px;
          border-radius: 16px;
          background: var(--bg-sub);
          border: 2px dashed var(--border-hover);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .logo-img-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .logo-placeholder {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-align: center;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .grid-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }
        .form-hint {
          display: block;
          font-size: 11px;
          color: #94a3b8;
          margin-top: 4px;
        }
        .mt-20 { margin-top: 20px; }
        
        /* Switch styling */
        .switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }
        .switch input { 
          opacity: 0;
          width: 0;
          height: 0;
        }
        .slider {
          position: absolute;
          cursor: pointer;
          inset: 0;
          background-color: #cbd5e1;
          transition: .3s;
        }
        .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .3s;
        }
        input:checked + .slider {
          background-color: #10b981;
        }
        input:checked + .slider:before {
          transform: translateX(20px);
        }
        .slider.round {
          border-radius: 24px;
        }
        .slider.round:before {
          border-radius: 50%;
        }

        /* Working Hours list */
        .hours-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .hours-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 20px;
          border-radius: 12px;
          background: var(--bg-sub);
          border: 1px solid var(--border);
          transition: all 0.2s ease;
        }
        .hours-row.active-day {
          background: rgba(var(--tertiary-rgb), 0.05);
          border-color: rgba(var(--tertiary-rgb), 0.25);
        }
        .hours-day-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .day-name {
          font-weight: 600;
          color: var(--text);
        }
        .hours-time-picker {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .time-input {
          width: 110px !important;
          text-align: center;
        }
        .time-sep {
          color: #64748b;
          font-weight: 500;
        }
        .day-closed-badge {
          font-size: 12px;
          font-weight: 600;
          color: var(--danger);
          background: var(--danger-light);
          padding: 4px 10px;
          border-radius: 8px;
          border: 1px solid rgba(244, 63, 94, 0.2);
        }

        /* Notifications */
        .notifications-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .notif-toggle-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: var(--bg-sub);
          border-radius: 12px;
          border: 1px solid var(--border);
        }
        .notif-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .notif-label {
          font-weight: 600;
          color: var(--text);
        }
        .notif-desc {
          font-size: 12px;
          color: #64748b;
        }

        /* Integration Settings */
        .integration-group {
          background: var(--bg-sub);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px;
        }
        .int-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--text);
          margin-top: 0;
          margin-bottom: 16px;
          border-bottom: 1px solid var(--border);
          padding-bottom: 10px;
        }

        /* Data management & subcards */
        .sub-card {
          background: var(--bg-sub);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .card-sub-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
          margin: 0;
        }
        .sub-desc {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }
        .btn-group-row {
          display: flex;
          gap: 10px;
          margin-top: 8px;
        }
        .file-upload-box {
          margin-top: 8px;
        }
        .file-input-hidden {
          display: none;
        }
        .file-label-btn {
          cursor: pointer;
          display: inline-block;
          text-align: center;
        }
        .import-status-text {
          font-size: 11px;
          font-weight: 600;
          color: var(--tertiary);
          margin: 4px 0 0;
        }

        /* System Info */
        .system-info {
          background: var(--bg-sub);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid var(--border);
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          color: var(--text-muted);
          font-size: 13px;
        }
        .info-value {
          font-weight: 700;
          color: var(--text);
          font-size: 13px;
        }

        /* Users and modals */
        .users-table {
          overflow-x: auto;
          margin-top: 10px;
        }
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(3, 4, 7, 0.85);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal-box {
          background: var(--bg-card);
          border: 1px solid var(--border-hover);
          color: var(--text);
          border-radius: 20px;
          padding: 30px;
          width: 100%;
          max-width: 540px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          animation: scaleUp 0.2s ease-out;
        }
        @keyframes scaleUp {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .modal-error-box {
          background: var(--danger-light);
          border: 1px solid rgba(244, 63, 94, 0.2);
          border-radius: 10px;
          padding: 10px 14px;
          color: var(--danger);
          font-size: 13px;
          margin-bottom: 16px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}

export default Settings;
